"use client";

// Global feedback widget — "app users can give feedback on any page/section."
// A Hotjar-style floating trigger, mounted once in app/layout.tsx so it's
// available everywhere: 1-5 stars + free text, tagged with the page it was
// left on automatically. Bottom-LEFT corner deliberately — every page's
// success/action toasts already live bottom-right, so the two never collide.
//
// This widget WRITES feedback. It no longer reads it: the admin-only expander
// that used to hang off the bottom of this panel existed because there was no
// feedback route to link to, and it showed half the story (the stars, never the
// grade corrections) in a 224px-tall scroller. /feedback is that route, it holds
// both channels, and a second place to read the same list is a second place to
// keep in step.

import { useEffect, useRef, useState } from "react";
import { holdFullLoad } from "@/lib/stale-tab";
import { usePathname } from "next/navigation";
import { MessageSquarePlus, Star, X, Check } from "lucide-react";
import { submitFeedback } from "@/lib/ironside-feedback-store";

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= (hover || value);
        return (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onClick={() => onChange(n)}
            aria-label={`${n} star${n !== 1 ? "s" : ""}`}
            className="transition-transform hover:scale-110"
          >
            <Star size={22} className={filled ? "text-amber-400" : "text-line-strong"} fill={filled ? "currentColor" : "none"} />
          </button>
        );
      })}
    </div>
  );
}

export function FeedbackWidget({ inline = false }: { inline?: boolean }) {
  const pathname = usePathname();
  // Dense creator/brand reports provide an in-flow mobile trigger. Floating
  // feedback otherwise covers the follow-up filters or table actions.
  const report = pathname?.startsWith('/throughput/creators/') || pathname?.startsWith('/throughput/brands/');
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // The panel lives in the root layout, so a client navigation keeps it open
  // with its words; a stale tab's document-load navigation would not
  // (lib/stale-tab.ts). While it holds a rating or words, links stay client
  // navigations.
  const holding = useRef(false);
  useEffect(() => {
    holding.current = open && !submitted && (stars > 0 || message.trim() !== "");
  });
  useEffect(() => holdFullLoad(() => holding.current), []);

  const reset = () => {
    setStars(0);
    setMessage("");
    setSubmitted(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const submit = () => {
    if (stars === 0) return;
    submitFeedback(pathname, stars, message);
    setSubmitted(true);
    setTimeout(close, 1600);
  };

  return (
    <div className={inline ? 'relative md:hidden' : `${report ? 'hidden md:block ' : ''}fixed bottom-6 left-6 z-50`}>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="feedback-widget-trigger"
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11.5px] font-semibold text-onfill bg-accent-strong shadow-lg transition-transform hover:scale-105"
        >
          <MessageSquarePlus size={14} /> Feedback
        </button>
      )}

      {open && (
        <div className="w-80 max-w-full bg-surface border border-line rounded-lg shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-hairline">
            <span className="text-[12px] font-semibold text-fg">Feedback on this page</span>
            <button onClick={close} className="text-text-dim hover:text-text-muted transition-colors">
              <X size={14} />
            </button>
          </div>

          {submitted ? (
            <div className="px-4 py-6 text-center">
              <Check size={20} className="mx-auto mb-2 text-green-400" />
              <div className="text-[12px] text-fg font-medium">Thanks — that&apos;s logged.</div>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <div className="text-[10px] text-text-dim font-mono truncate" title={pathname}>{pathname}</div>
              <StarPicker value={stars} onChange={setStars} />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="What's working, what's not, or what would make this easier?"
                className="w-full bg-panel border border-hairline rounded-lg px-3 py-2 text-[12px] text-text focus:outline-none focus:border-accent/50 resize-none placeholder:text-text-dim"
              />
              <button
                onClick={submit}
                disabled={stars === 0}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-onfill bg-accent-strong transition-colors disabled:opacity-40"
              >
                Send feedback
              </button>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
